import type { Position } from "@xyflow/react";
import type { Rect } from "../cable-escape";
import { sideDir, type Pt } from "../cable-routing";
import { GEOMETRY } from "../canvas-geometry";

export interface OrthogonalRouteRequest {
  source: Pt;
  sourceSide: Position;
  target: Pt;
  targetSide: Position;
  hard: readonly Rect[];
  self?: readonly Rect[];
  others?: readonly (readonly Pt[])[];
  soft?: readonly Rect[];
  sourceStub?: number;
  targetStub?: number;
}

const EPS = 1e-7;
const BEND_COST = 40;
const CLEARANCE_COST = 4;
const MAX_RECTS = 512;
const MAX_LINES = 44;
const MAX_EXPANSIONS = 6_000;
const MAX_OTHER_SEGMENTS = 256;
const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];
const OPPOSITE = [1, 0, 3, 2];
type PricedRect = { rect: Rect; weight: number };
type Segment = { a: Pt; b: Pt };

const same = (a: Pt, b: Pt): boolean => a.x === b.x && a.y === b.y;
const distance = (a: Pt, b: Pt): number => Math.abs(b.x - a.x) + Math.abs(b.y - a.y);
const direction = (a: Pt, b: Pt): number => b.x !== a.x ? (b.x > a.x ? 0 : 1) : (b.y > a.y ? 2 : 3);
const sideIndex = (p: Pt): number => p.x ? (p.x > 0 ? 0 : 1) : (p.y > 0 ? 2 : 3);
const validPoint = (p: Pt): boolean => Number.isFinite(p.x) && Number.isFinite(p.y);
const validRect = (r: Rect): boolean => validPoint(r) && Number.isFinite(r.w) && Number.isFinite(r.h) && r.w >= 0 && r.h >= 0;
const strictlyContains = (r: Rect, p: Pt): boolean => p.x > r.x + EPS && p.x < r.x + r.w - EPS && p.y > r.y + EPS && p.y < r.y + r.h - EPS;

/** Interior overlap only: touching a body boundary is legal, crossing it is not. */
function overlap(a: Pt, b: Pt, r: Rect): number {
  if (a.y === b.y) {
    if (a.y <= r.y + EPS || a.y >= r.y + r.h - EPS) return 0;
    return Math.max(0, Math.min(Math.max(a.x, b.x), r.x + r.w) - Math.max(Math.min(a.x, b.x), r.x));
  }
  if (a.x <= r.x + EPS || a.x >= r.x + r.w - EPS) return 0;
  return Math.max(0, Math.min(Math.max(a.y, b.y), r.y + r.h) - Math.max(Math.min(a.y, b.y), r.y));
}

/** Unlike the general drawing simplifier, never erase a reversal or tiny elbow. */
function compact(points: readonly Pt[]): Pt[] {
  const result: Pt[] = [];
  for (const p of points) {
    if (result.length && same(result[result.length - 1], p)) continue;
    while (result.length >= 2) {
      const a = result[result.length - 2];
      const b = result[result.length - 1];
      if (direction(a, b) !== direction(b, p)) break;
      result.pop();
    }
    result.push(p);
  }
  return result;
}

function endpointAnchor(point: Pt, dir: Pt, stub: number, hard: readonly Rect[], self: readonly Rect[]): Pt {
  let length = Math.max(0, stub);
  for (const item of [...hard, ...self]) {
    const r = item.core ?? item;
    if (strictlyContains(r, point)) continue;
    let room: number;
    if (dir.x) {
      if (point.y <= r.y || point.y >= r.y + r.h) continue;
      room = dir.x > 0 ? r.x - point.x : point.x - r.x - r.w;
    } else {
      if (point.x <= r.x || point.x >= r.x + r.w) continue;
      room = dir.y > 0 ? r.y - point.y : point.y - r.y - r.h;
    }
    if (room >= 0) length = Math.min(length, Math.max(0, room - GEOMETRY.STUB_CLEARANCE));
  }
  for (const item of self) {
    const r = item.core ?? item;
    if (!strictlyContains(r, point)) continue;
    // A junction handle is at the centre of its own body. Only this straight,
    // outward terminal segment may leave that body; the search cannot enter it.
    const exit = dir.x > 0 ? r.x + r.w - point.x : dir.x < 0 ? point.x - r.x : dir.y > 0 ? r.y + r.h - point.y : point.y - r.y;
    if (exit > EPS) length = Math.max(length, exit + 1);
  }
  return { x: point.x + dir.x * length, y: point.y + dir.y * length };
}

function stubClear(point: Pt, anchor: Pt, hard: readonly Rect[], self: readonly Rect[]): boolean {
  for (const r of hard) if (overlap(point, anchor, r.core ?? r) > EPS) return false;
  for (const item of self) {
    const r = item.core ?? item;
    if (!strictlyContains(r, point) && overlap(point, anchor, r) > EPS) return false;
  }
  return true;
}

function crosses(a: Pt, b: Pt, other: Segment): boolean {
  const horizontal = a.y === b.y;
  if (horizontal === (other.a.y === other.b.y)) return false;
  const h = horizontal ? { a, b } : other;
  const v = horizontal ? other : { a, b };
  return v.a.x > Math.min(h.a.x, h.b.x) + EPS && v.a.x < Math.max(h.a.x, h.b.x) - EPS &&
    h.a.y > Math.min(v.a.y, v.b.y) + EPS && h.a.y < Math.max(v.a.y, v.b.y) - EPS;
}

function otherSegments(polylines: OrthogonalRouteRequest["others"]): Segment[] {
  const result: Segment[] = [];
  let examined = 0;
  for (const polyline of (polylines ?? []).slice(0, MAX_OTHER_SEGMENTS)) {
    for (let i = 1; i < polyline.length; i++) {
      const a = polyline[i - 1];
      const b = polyline[i];
      if (!same(a, b) && validPoint(a) && validPoint(b) && (a.x === b.x || a.y === b.y)) result.push({ a, b });
      if (++examined === MAX_OTHER_SEGMENTS) return result;
    }
  }
  return result;
}

/** Fast ordinary connectors, ordered deterministically when costs tie. */
function simpleRoutes(a: Pt, b: Pt): Pt[][] {
  const xMid = (a.x + b.x) / 2;
  const yMid = (a.y + b.y) / 2;
  const routes = [
    [a, { x: b.x, y: a.y }, b],
    [a, { x: a.x, y: b.y }, b],
  ];
  for (const x of [xMid, Math.min(a.x, b.x) - GEOMETRY.STUB, Math.max(a.x, b.x) + GEOMETRY.STUB]) {
    routes.push([a, { x, y: a.y }, { x, y: b.y }, b]);
  }
  for (const y of [yMid, Math.min(a.y, b.y) - GEOMETRY.STUB, Math.max(a.y, b.y) + GEOMETRY.STUB]) {
    routes.push([a, { x: a.x, y }, { x: b.x, y }, b]);
  }
  return routes;
}

function routeCost(points: readonly Pt[], sDir: number, tDir: number, blockers: readonly Rect[], priced: readonly PricedRect[], others: readonly Segment[], sourceHasStub: boolean, targetHasStub: boolean): { base: number; extra: number } | null {
  if (points.length < 2 || direction(points[0], points[1]) !== sDir || direction(points[points.length - 2], points[points.length - 1]) !== OPPOSITE[tDir]) return null;
  let base = 0;
  let extra = 0;
  let previous = sDir;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a.x !== b.x && a.y !== b.y) return null;
    const dir = direction(a, b);
    if (dir === OPPOSITE[previous]) return null;
    base += distance(a, b) + (dir !== previous ? BEND_COST : 0);
    previous = dir;
    // Own terminal stubs have already been checked separately.
    if (!(i === 1 && sourceHasStub) && !(i === points.length - 1 && targetHasStub)) {
      for (const r of blockers) if (overlap(a, b, r) > EPS) return null;
      for (const { rect, weight } of priced) extra += overlap(a, b, rect) * weight;
    }
    for (const other of others) if (crosses(a, b, other)) extra += GEOMETRY.CROSSING_PENALTY;
  }
  return { base, extra };
}

function lowerBound(values: readonly number[], value: number): number {
  let lo = 0;
  let hi = values.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (values[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function axisLines(a: number, b: number, rects: readonly Rect[], axis: "x" | "y"): number[] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const anchors = new Set([a, b, (a + b) / 2, lo - GEOMETRY.STUB, hi + GEOMETRY.STUB]);
  const lines = new Set(anchors);
  for (const r of rects) {
    const start = axis === "x" ? r.x : r.y;
    const end = start + (axis === "x" ? r.w : r.h);
    // Body boundaries preserve narrow corridors. Clearance boundaries provide
    // comfortable lanes when space allows; neither changes hard geometry.
    lines.add(start);
    lines.add(end);
    lines.add(start - 1);
    lines.add(end + 1);
  }
  const spanDistance = (n: number): number => Math.max(lo - n, n - hi, 0);
  const result = [...lines];
  if (result.length > MAX_LINES) {
    result.sort((p, q) => Number(anchors.has(q)) - Number(anchors.has(p)) || spanDistance(p) - spanDistance(q) || Math.abs(p - (lo + hi) / 2) - Math.abs(q - (lo + hi) / 2) || p - q);
    result.length = MAX_LINES;
  }
  return result.sort((p, q) => p - q);
}

/** Rasterize only the intersected grid span, not every edge for every rect. */
function markRect(r: Rect, xs: readonly number[], ys: readonly number[], h: Uint8Array | Float64Array, v: Uint8Array | Float64Array, weight = 0): void {
  const nx = xs.length;
  const rowStart = lowerBound(ys, r.y + EPS);
  const rowEnd = lowerBound(ys, r.y + r.h - EPS);
  const colStart = lowerBound(xs, r.x + EPS);
  const colEnd = lowerBound(xs, r.x + r.w - EPS);
  const hStart = Math.max(0, lowerBound(xs, r.x + EPS) - 1);
  const hEnd = Math.min(nx - 1, lowerBound(xs, r.x + r.w - EPS));
  const vStart = Math.max(0, lowerBound(ys, r.y + EPS) - 1);
  const vEnd = Math.min(ys.length - 1, lowerBound(ys, r.y + r.h - EPS));
  for (let row = rowStart; row < rowEnd; row++) {
    for (let col = hStart; col < hEnd; col++) {
      const idx = row * (nx - 1) + col;
      if (weight) h[idx] += Math.max(0, Math.min(xs[col + 1], r.x + r.w) - Math.max(xs[col], r.x)) * weight;
      else h[idx] = 1;
    }
  }
  for (let col = colStart; col < colEnd; col++) {
    for (let row = vStart; row < vEnd; row++) {
      const idx = row * nx + col;
      if (weight) v[idx] += Math.max(0, Math.min(ys[row + 1], r.y + r.h) - Math.max(ys[row], r.y)) * weight;
      else v[idx] = 1;
    }
  }
}

class MinHeap {
  private states: number[] = [];
  private priorities: number[] = [];
  get size(): number { return this.states.length; }
  private less(a: number, b: number): boolean {
    return this.priorities[a] < this.priorities[b] || (this.priorities[a] === this.priorities[b] && this.states[a] < this.states[b]);
  }
  private swap(a: number, b: number): void {
    [this.states[a], this.states[b]] = [this.states[b], this.states[a]];
    [this.priorities[a], this.priorities[b]] = [this.priorities[b], this.priorities[a]];
  }
  push(state: number, priority: number): void {
    let i = this.states.length;
    this.states.push(state);
    this.priorities.push(priority);
    while (i > 0) {
      const parent = (i - 1) >>> 1;
      if (!this.less(i, parent)) break;
      this.swap(i, parent);
      i = parent;
    }
  }
  pop(): number {
    const state = this.states[0];
    const lastState = this.states.pop()!;
    const lastPriority = this.priorities.pop()!;
    if (this.states.length) {
      this.states[0] = lastState;
      this.priorities[0] = lastPriority;
      let i = 0;
      for (;;) {
        const left = 2 * i + 1;
        const right = left + 1;
        let best = i;
        if (left < this.states.length && this.less(left, best)) best = left;
        if (right < this.states.length && this.less(right, best)) best = right;
        if (best === i) break;
        this.swap(i, best);
        i = best;
      }
    }
    return state;
  }
}

/**
 * Direction-aware orthogonal routing with hard body exclusion. Common clear
 * connectors avoid grid allocation. More complex routes use a bounded Hanan
 * grid with length, bend, clearance, zone and cable-crossing costs.
 *
 * Bounds are deterministic (512 rectangles, 44 lines/axis, 6,000 expansions).
 * `null` means invalid input, a blocked terminal, or no route within that
 * budget. The caller owns the fallback; bodies are never shrunk or ignored.
 */
export function searchOrthogonalRoute(request: OrthogonalRouteRequest): Pt[] | null {
  const { source, target, hard, self = [], soft = [] } = request;
  const sourceStub = request.sourceStub ?? GEOMETRY.STUB;
  const targetStub = request.targetStub ?? GEOMETRY.STUB;
  if (!validPoint(source) || !validPoint(target) || !Number.isFinite(sourceStub) || !Number.isFinite(targetStub) || hard.length + self.length + soft.length > MAX_RECTS) return null;
  const all = [...hard, ...self];
  if ([...all, ...soft].some((r) => !validRect(r) || (r.core !== undefined && !validRect(r.core)))) return null;
  const sVector = sideDir(request.sourceSide);
  const tVector = sideDir(request.targetSide);
  const sDir = sideIndex(sVector);
  const tDir = sideIndex(tVector);
  const sa = endpointAnchor(source, sVector, sourceStub, hard, self);
  const ta = endpointAnchor(target, tVector, targetStub, hard, self);
  // A point has no terminal direction. Let the caller handle this degenerate
  // connector rather than returning a one-point "route" from the start state.
  if (same(source, target) && same(source, sa) && same(target, ta)) return null;
  if (!stubClear(source, sa, hard, self) || !stubClear(target, ta, hard, self)) return null;
  const blockers = all.map((r) => r.core ?? r);
  const priced: PricedRect[] = [
    ...all.filter((r) => r.core !== undefined).map((rect) => ({ rect, weight: CLEARANCE_COST })),
    ...soft.map((rect) => ({ rect, weight: GEOMETRY.ZONE_WALL_PENALTY / Math.max(1, Math.min(rect.w, rect.h)) })),
  ];
  const others = otherSegments(request.others);
  const arrival = OPPOSITE[tDir];
  const aligned = sVector.x ? source.y === target.y : source.x === target.x;
  const minimumBends = sDir === arrival ? (aligned ? 0 : 2) : ((sDir < 2) === (arrival < 2) ? 2 : 1);
  const lowerCost = distance(source, target) + minimumBends * BEND_COST;
  let bestSimple: Pt[] | null = null;
  let bestCost = Infinity;
  for (const middle of simpleRoutes(sa, ta)) {
    // Keep terminal anchors while checking collisions, even when collinear.
    const points = [source, ...compact(middle), target];
    if (same(source, sa)) points.shift();
    if (same(target, ta)) points.pop();
    const score = routeCost(points, sDir, tDir, blockers, priced, others, !same(source, sa), !same(target, ta));
    if (score && score.extra === 0 && score.base < bestCost) {
      if (score.base <= lowerCost + EPS) return compact(points);
      bestSimple = compact(points);
      bestCost = score.base;
    }
  }
  if (bestSimple) return bestSimple;

  const rects = [...blockers, ...priced.map(({ rect }) => rect)];
  const xs = axisLines(sa.x, ta.x, rects, "x");
  const ys = axisLines(sa.y, ta.y, rects, "y");
  const nx = xs.length;
  const ny = ys.length;
  const hBlocked = new Uint8Array((nx - 1) * ny);
  const vBlocked = new Uint8Array(nx * (ny - 1));
  const hCost = new Float64Array(hBlocked.length);
  const vCost = new Float64Array(vBlocked.length);
  for (const r of blockers) markRect(r, xs, ys, hBlocked, vBlocked);
  for (const { rect, weight } of priced) markRect(rect, xs, ys, hCost, vCost, weight);
  // Charge each crossing once using a half-open grid edge. Coincident parallel
  // segments remain available to the caller's later lane-separation pass.
  for (const { a, b } of others) {
    if (a.x === b.x) {
      const col = lowerBound(xs, a.x) - 1;
      if (col < 0 || col >= nx - 1) continue;
      for (let row = lowerBound(ys, Math.min(a.y, b.y) + EPS); row < lowerBound(ys, Math.max(a.y, b.y) - EPS); row++) hCost[row * (nx - 1) + col] += GEOMETRY.CROSSING_PENALTY;
    } else {
      const row = lowerBound(ys, a.y) - 1;
      if (row < 0 || row >= ny - 1) continue;
      for (let col = lowerBound(xs, Math.min(a.x, b.x) + EPS); col < lowerBound(xs, Math.max(a.x, b.x) - EPS); col++) vCost[row * nx + col] += GEOMETRY.CROSSING_PENALTY;
    }
  }
  const startNode = ys.indexOf(sa.y) * nx + xs.indexOf(sa.x);
  const goalNode = ys.indexOf(ta.y) * nx + xs.indexOf(ta.x);
  const stateCount = nx * ny * 4;
  // A virtual final state makes the last terminal bend part of A*'s ordering.
  const goalState = stateCount;
  const costs = new Float64Array(stateCount + 1).fill(Infinity);
  const parent = new Int32Array(stateCount + 1).fill(-1);
  const done = new Uint8Array(stateCount + 1);
  const startState = startNode * 4 + sDir;
  const heap = new MinHeap();
  costs[startState] = 0;
  heap.push(startState, distance(sa, ta));
  let expanded = 0;
  while (heap.size && expanded < MAX_EXPANSIONS) {
    const state = heap.pop();
    if (done[state]) continue;
    if (state === goalState) {
      const points: Pt[] = [];
      for (let cursor = parent[goalState]; cursor !== -1; cursor = parent[cursor]) {
        const node = cursor >>> 2;
        points.push({ x: xs[node % nx], y: ys[(node / nx) | 0] });
      }
      points.reverse();
      return compact([source, ...points, target]);
    }
    done[state] = 1;
    expanded++;
    const node = state >>> 2;
    const dir = state & 3;
    const col = node % nx;
    const row = (node / nx) | 0;
    if (node === goalNode && dir !== tDir && (!same(target, ta) || dir === OPPOSITE[tDir])) {
      const cost = costs[state] + (dir === OPPOSITE[tDir] ? 0 : BEND_COST);
      if (cost < costs[goalState]) {
        costs[goalState] = cost;
        parent[goalState] = state;
        heap.push(goalState, cost);
      }
    }
    for (let nextDir = 0; nextDir < 4; nextDir++) {
      if (nextDir === OPPOSITE[dir] || (state === startState && same(source, sa) && nextDir !== sDir)) continue;
      const x = col + DX[nextDir];
      const y = row + DY[nextDir];
      if (x < 0 || x >= nx || y < 0 || y >= ny) continue;
      const horizontal = DX[nextDir] !== 0;
      const edge = horizontal ? row * (nx - 1) + Math.min(x, col) : Math.min(y, row) * nx + col;
      if (horizontal ? hBlocked[edge] : vBlocked[edge]) continue;
      const next = (y * nx + x) * 4 + nextDir;
      if (done[next]) continue;
      const length = horizontal ? Math.abs(xs[x] - xs[col]) : Math.abs(ys[y] - ys[row]);
      const cost = costs[state] + length + (nextDir === dir ? 0 : BEND_COST) + (horizontal ? hCost[edge] : vCost[edge]);
      if (cost >= costs[next]) continue;
      costs[next] = cost;
      parent[next] = state;
      heap.push(next, cost + Math.abs(xs[x] - ta.x) + Math.abs(ys[y] - ta.y));
    }
  }
  return null;
}
